import { defineStore } from 'pinia';
import { useOfferSheetStore, type OfferSheet } from '@/stores/offer-sheet';
import { useFormsStore } from 'vuniform';
import { formIds } from '@/helpers/formIds';

type OfferSheetFormState = unknown;

interface TicketForm {
  _formId: string;
  total: number;
}
interface ExpenseCategoryForm {
  _formId: string;
  total: number;
}

export type FormId = string;

export const TAXES_FORM_ID = 'taxes-form-id';
export const PROMOTER_PROFIT_FORM_ID = 'promoter-profit-form-id';
export const EVENT_NAME_FORM_ID = 'event-name-form-id';
export const PROMOTER_NAME_FORM_ID = 'promoter-name-form-id';
export const NOTE_FORM_ID = 'note-form-id';

const ticketFormIdPrefix = 'ticket-row-form';
const listFormIdPrefix = 'list-row-form';
const contactFormIdPrefix = 'contact-row-form';
const expenseCategoryFormIdPrefix = 'expense-category-row-form';
const expenseFormIdPrefix = 'expense-row-form';

const getTicketFormKey = (k: unknown): string => `${ticketFormIdPrefix}-${k}`;
const getListFormKey = (k: unknown): string => `${listFormIdPrefix}-${k}`;
const getContactFormKey = (k: unknown): string => `${contactFormIdPrefix}-${k}`;
const getExpenseCategoryFormKey = (k: unknown): string => `${expenseCategoryFormIdPrefix}-${k}`;
const getExpenseFormKey = (k: unknown): string => `${expenseFormIdPrefix}-${k}`;

export const useOfferSheetFormStore = defineStore('offerSheetForm', {
  state: (): OfferSheetFormState => ({}),

  getters: {
    ticketFormIds(_: OfferSheetFormState): string[] {
      return formIds(getTicketFormKey);
    },

    ticketsRows(): TicketForm[] {
      const formsStore = useFormsStore();
      return this.ticketFormIds.map(k => {
        const price = Number.parseFloat(<string>formsStore.fieldGetValue(k, 'price'));
        const projectedSold = Number.parseInt(<string>formsStore.fieldGetValue(k, 'projected'));
        return {
          _formId: k,
          total: price * projectedSold,
        };
      });
    },

    ticketsTotalGBOR(): number {
      return this.ticketsRows
        .filter(({ total }) => !isNaN(total))
        .reduce((sum: number, { total }: TicketForm): number => {
          return sum + total;
        }, 0);
    },

    projectedTicketsSoldTotal(): number {
      const formsStore = useFormsStore();
      return this.ticketsRows
        .filter(({ _formId: k }) => !isNaN(<number>formsStore.fieldGetValue(k, 'projected')))
        .reduce((sum: number, { _formId: k }: TicketForm): number => {
          return sum + Number.parseFloat(<string>formsStore.fieldGetValue(k, 'projected'));
        }, 0);
    },

    listsFormIds(): string[] {
      return formIds(getListFormKey);
    },

    listsTotalGBOR(): number {
      const formsStore = useFormsStore();
      return this.listsFormIds
        .filter(k => !isNaN(<number>formsStore.fieldGetValue(k, 'gross')))
        .reduce((sum: number, k: string): number => {
          return sum + Number.parseFloat(<string>formsStore.fieldGetValue(k, 'gross'));
        }, 0);
    },

    projectedTicketsFromLists(): number {
      const formsStore = useFormsStore();
      return this.listsFormIds
        .filter(k => !isNaN(<number>formsStore.fieldGetValue(k, 'projected')))
        .reduce((sum: number, k: string): number => {
          return sum + Number.parseFloat(<string>formsStore.fieldGetValue(k, 'projected'));
        }, 0);
    },

    totalCapAmount(): number {
      return this.listsTotalGBOR + this.ticketsTotalGBOR;
    },

    totalCapTickets(): number {
      return this.projectedTicketsFromLists + this.projectedTicketsSoldTotal;
    },

    taxAmount(): number {
      return this.totalCapAmount * (this.taxPercentage / 100.0);
    },

    netPotential(): number {
      return this.totalCapAmount - this.taxAmount;
    },

    taxPercentage(): number {
      const formsStore = useFormsStore();
      const taxPercentage = Number.parseFloat(<string>formsStore.fieldGetValue(TAXES_FORM_ID, 'taxPercentage'));
      return taxPercentage;
    },

    contactsFormIds(): string[] {
      return formIds(getContactFormKey);
    },

    expenseCategoryFormIds(_: OfferSheetFormState): string[] {
      return formIds(getExpenseCategoryFormKey);
    },

    expenseCategoryRows(): ExpenseCategoryForm[] {
      return this.expenseCategoryFormIds.map(k => {
        const catAmount = this.expenseFormsKeys[k]
          .filter(e => !isNaN(<number> this.expenseAmount(e)))
          .reduce((sum: number, e: string): number => {
            return sum + <number> this.expenseAmount(e);
          }, 0);

        return {
          _formId: k,
          total: catAmount,
        };
      });
    },

    totalExpenses(): number {
      return this.expenseCategoryRows
        .filter(({ total }) => !isNaN(total))
        .reduce((sum: number, { total }: ExpenseCategoryForm): number => {
          return sum + total;
        }, 0);
    },

    expenseFormsKeys(): { [k: string]: string[] } {
      return this.expenseFormIds;
    },

    expenseFormIds(_: OfferSheetFormState): { [k: string]: string[] } {
      return this.expenseCategoryFormIds.reduce((acc, key) => {
        return {
          ...acc,
          [key]: formIds((k: unknown) => `${key}-${getExpenseFormKey(k)}`),
        };
      }, {});
    },

    expenseAmount(): (formId: FormId) => number | undefined {
      return (formId: string): number | undefined => {
        const formsStore = useFormsStore();
        const calculationType = <string>formsStore.fieldGetValue(formId, 'calculationType');
        const calculationValue = <number>formsStore.fieldGetValue(formId, 'calculationValue');

        if (!calculationType) {
          return <number>formsStore.fieldGetValue(formId, 'amount');
        }
        switch (calculationType) {
          case 'gbor_percentage':
            return this.ticketsTotalGBOR * (calculationValue / 100);

          case 'per_ticket':
            return this.projectedTicketsSoldTotal * calculationValue;

          case 'per_attendee':
            return calculationValue * (this.projectedTicketsSoldTotal + this.projectedTicketsFromLists);
        }
      };
    },

    promoterProfitPercentage(): number {
      const formsStore = useFormsStore();
      const promoterProfitPercentage = Number.parseFloat(
        <string>formsStore.fieldGetValue(PROMOTER_PROFIT_FORM_ID, 'promoterProfitPercentage'),
      );
      return promoterProfitPercentage;
    },

    promoterProfit(): number {
      return ((this.promoterProfitPercentage || 0) * this.totalExpenses) / 100;
    },
  },

  actions: {
    initFromOfferSheet() {
      this.$reset();
      const offerSheetStore = useOfferSheetStore();
      const offerSheet: OfferSheet | undefined = offerSheetStore.offerSheet;
      if (!offerSheet) {
        return;
      }
    },

    deleteTicketRow(formId: string) {
      const formsStore = useFormsStore();
      formsStore.SET_INITIAL_VALUES({ formId, values: {}, clearDirty: true });
      formsStore.RESET_FIELD_VALUES(formId);
    },

    deleteListRow(formId: string) {
      const formsStore = useFormsStore();
      formsStore.SET_INITIAL_VALUES({ formId, values: {}, clearDirty: true });
      formsStore.RESET_FIELD_VALUES(formId);
    },

    deleteContactsRow(formId: string) {
      const formsStore = useFormsStore();
      formsStore.SET_INITIAL_VALUES({ formId, values: {}, clearDirty: true });
      formsStore.RESET_FIELD_VALUES(formId);
    },

    deleteExpenseCategoryRow(formId: string) {
      const formsStore = useFormsStore();
      formsStore.SET_INITIAL_VALUES({ formId, values: {}, clearDirty: true });
      formsStore.RESET_FIELD_VALUES(formId);
    },

    deleteExpenseRow(formId: string) {
      const formsStore = useFormsStore();
      formsStore.SET_INITIAL_VALUES({ formId, values: {}, clearDirty: true });
      formsStore.RESET_FIELD_VALUES(formId);
    },
  },
});
